/* Google Maps, loaded when it is needed - not before the first screen (27 Oct 2026).

   The whole app sat inside <LoadScript>, which draws nothing until Google's Maps script
   has downloaded. On a slow phone (Fast 3G) that kept the screen blank for ~18 seconds,
   every visit, Home included - and only the location fields and pickers use Maps. Now:
   the app draws at once; Maps loads in the background a little after the first screen
   (so a pickup field is ready by the time someone types), or at once when a page that
   needs it opens first (useGoogleMaps). */

import { useEffect, useState } from "react";

const READY = () => !!(typeof window !== "undefined" && window.google?.maps?.places);
let pending = null;

/* Google's script adds a Roboto stylesheet the app never uses (LoadScript's
   preventGoogleFontsLoading did the same): one request fewer on a slow line. */
function skipRobotoFont() {
  const head = document.head;
  if (!head || head.__sramikaNoRoboto) return;
  const insert = head.insertBefore.bind(head);
  head.insertBefore = (node, ref) => {
    if (node?.href && String(node.href).includes("fonts.googleapis.com/css?family=Roboto")) return node;
    return insert(node, ref);
  };
  head.__sramikaNoRoboto = true;
}

/** Loads Google Maps (with Places) once; resolves with window.google. */
export function loadGoogleMaps() {
  if (READY()) return Promise.resolve(window.google);
  if (pending) return pending;
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  pending = new Promise((resolve, reject) => {
    const cb = "__sramikaMapsReady";
    window[cb] = () => resolve(window.google);
    skipRobotoFont();
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key || "")}&libraries=places&loading=async&callback=${cb}`;
    s.onerror = () => { pending = null; reject(new Error("Google Maps did not load")); };
    document.head.appendChild(s);
  });
  return pending;
}

/** In the background, once the first screen is up and the browser is idle. */
export function loadGoogleMapsSoon(delayMs = 2500) {
  if (typeof window === "undefined") return;
  const idle = window.requestIdleCallback || ((f) => setTimeout(f, 200));
  setTimeout(() => idle(() => loadGoogleMaps().catch(() => {})), delayMs);
}

/** true once Maps is ready; starts the load if it has not started. */
export function useGoogleMaps() {
  const [ready, setReady] = useState(READY);
  useEffect(() => {
    if (ready) return undefined;
    let live = true;
    loadGoogleMaps().then(() => { if (live) setReady(true); }).catch(() => {});
    return () => { live = false; };
  }, [ready]);
  return ready;
}
